import { useEffect, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useProfiles } from "@/hooks/auth/useProfiles";
import { useSessions } from "@/hooks/brain/useSessions";
import { cn } from "@/lib/utils";
import type { SessionSentiment } from "@/types/brain/session";
import { SessionDetailDrawer } from "./SessionDetailDrawer";
import { UnlinkedInvocationsDrawer } from "./UnlinkedInvocationsDrawer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const pageSize = 20;

const getSentimentBadge = (sentiment: SessionSentiment) => {
  if (sentiment === "positive") {
    return (
      <Badge className="bg-genie-primary-light text-genie-primary border-genie-primary/20 border text-xs">
        Positive
      </Badge>
    );
  }
  if (sentiment === "negative") {
    return (
      <Badge className="bg-genie-error-light text-genie-error border-genie-error/20 border text-xs">Negative</Badge>
    );
  }
  return (
    <Badge className="bg-genie-background-muted text-genie-text-muted border-genie-border border text-xs">
      Unknown
    </Badge>
  );
};

const BrainChatLogsPage = () => {
  const { user: authUser } = useAuth();
  const { data: profiles = [] } = useProfiles();

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const [showUnlinked, setShowUnlinked] = useState(false);

  useEffect(() => {
    if (authUser?.id && !selectedUserId) {
      setSelectedUserId(authUser.id);
    }
  }, [authUser?.id, selectedUserId]);

  useEffect(() => {
    setPage(1);
  }, [selectedUserId, searchTerm]);

  const { data, isLoading, isError, refetch, isFetching } = useSessions(selectedUserId, {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: searchTerm || undefined,
  });

  const sessions = data?.sessions ?? [];
  const count = data?.count ?? 0;

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <GenieIcon name="brain" size={32} className="h-8 w-8" />
          <div>
            <h1 className="text-3xl font-bold text-genie-text-primary">Brain Chat Logs</h1>
            <p className="text-genie-text-secondary">
              Browse Help Genie core (brain) chat sessions, and trace each message's tool invocations
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {profiles
                  .slice()
                  .sort((a, b) => (a.full_name ?? a.email ?? "").localeCompare(b.full_name ?? b.email ?? ""))
                  .map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name ? `${p.full_name} (${p.email})` : p.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search by message content or session ID..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              {selectedUserId && (
                <p className="text-sm text-genie-text-secondary">
                  {count} session{count !== 1 ? "s" : ""} found
                </p>
              )}
              {selectedUserId && (
                <button
                  onClick={() => setShowUnlinked(true)}
                  className="text-sm text-genie-text-muted hover:text-genie-primary underline underline-offset-2"
                >
                  View unlinked invocations
                </button>
              )}
            </div>
            <Button
              onClick={() => refetch()}
              disabled={isFetching || !selectedUserId}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              Reload
            </Button>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-lg border border-genie-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Preview</TableHead>
                <TableHead>Messages</TableHead>
                <TableHead>Tool Invocations</TableHead>
                <TableHead>Sentiment</TableHead>
                <TableHead>Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!selectedUserId ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-genie-text-secondary">
                    Select a user to view their chat sessions
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-genie-text-secondary">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Loading sessions...
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-genie-text-muted">
                    Failed to load chat sessions
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-genie-text-muted">
                    No chat sessions found
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow
                    key={session.session_id}
                    className="cursor-pointer hover:bg-genie-background-muted"
                    onClick={() => setOpenSessionId(session.session_id)}
                  >
                    <TableCell className="max-w-md truncate text-sm text-genie-text-primary">
                      {session.preview}
                    </TableCell>
                    <TableCell className="text-sm text-genie-text-secondary">{session.message_count}</TableCell>
                    <TableCell className="text-sm text-genie-text-secondary">
                      {session.tool_invocation_count}
                    </TableCell>
                    <TableCell>{getSentimentBadge(session.sentiment)}</TableCell>
                    <TableCell className="text-sm text-genie-text-secondary whitespace-nowrap">
                      {new Date(session.last_message_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {selectedUserId && count > pageSize && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="order-2 sm:order-1 text-genie-text-secondary">
                Page <span className="font-semibold">{page}</span>
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * pageSize >= count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <SessionDetailDrawer sessionId={openSessionId} userId={selectedUserId} onClose={() => setOpenSessionId(null)} />
      <UnlinkedInvocationsDrawer userId={showUnlinked ? selectedUserId : null} onClose={() => setShowUnlinked(false)} />
    </div>
  );
};

export default BrainChatLogsPage;
